import { useState, useEffect } from 'react'
import { OSM_TEXT } from '../lib/legal'
import { useAuth } from '../hooks/useAuth'
import { fetchRows, createRow } from '../lib/api'

const getVal = v => (v && typeof v === 'object' && 'value' in v) ? (v.value ?? '') : (v ?? '')

function TideCurve({ extremes }) {
  const pts = (extremes || []).slice(0, 12).map(e => ({ t: new Date(e.time).getTime(), h: e.heightM }))
  if (pts.length < 2) return null
  const W = 640, H = 120, padL = 8, padR = 8, padT = 14, padB = 22
  const t0 = pts[0].t, t1 = pts[pts.length - 1].t
  const hs = pts.map(p => p.h)
  const hMin = Math.min(...hs), hMax = Math.max(...hs)
  const hRange = (hMax - hMin) || 1
  const x = t => padL + ((t - t0) / (t1 - t0)) * (W - padL - padR)
  const y = h => padT + (1 - (h - hMin) / hRange) * (H - padT - padB)
  // smooth path via Catmull-Rom-ish using quadratic midpoints
  let d = 'M ' + x(pts[0].t).toFixed(1) + ' ' + y(pts[0].h).toFixed(1)
  for (let i = 1; i < pts.length; i++) {
    const xm = (x(pts[i-1].t) + x(pts[i].t)) / 2
    d += ' Q ' + x(pts[i-1].t).toFixed(1) + ' ' + y(pts[i-1].h).toFixed(1) + ' ' + xm.toFixed(1) + ' ' + ((y(pts[i-1].h)+y(pts[i].h))/2).toFixed(1)
  }
  d += ' T ' + x(pts[pts.length-1].t).toFixed(1) + ' ' + y(pts[pts.length-1].h).toFixed(1)
  const area = d + ' L ' + x(pts[pts.length-1].t).toFixed(1) + ' ' + (H - padB) + ' L ' + x(pts[0].t).toFixed(1) + ' ' + (H - padB) + ' Z'
  const fmtT = ms => new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  return (
    <svg viewBox={'0 0 ' + W + ' ' + H} width="100%" style={{ display: 'block' }}>
      <path d={area} fill="rgba(106,170,212,0.12)" />
      <path d={d} fill="none" stroke="#2a6a96" strokeWidth="2" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={x(p.t)} cy={y(p.h)} r="3" fill={p.h === hMax || i % 2 === 0 ? '#2a6a96' : '#8a5c0a'} />
          <text x={x(p.t)} y={H - 8} textAnchor="middle" fontSize="8" fill="var(--muted)">{fmtT(p.t)}</text>
        </g>
      ))}
    </svg>
  )
}

const tideCache = {}

function groupTides(extremes) {
  const byDay = {}
  ;(extremes || []).forEach(e => {
    const dt = new Date(e.time)
    const dayKey = dt.toISOString().split('T')[0]
    if (!byDay[dayKey]) byDay[dayKey] = { day: dt.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }), events: [] }
    byDay[dayKey].events.push({ type: e.type, heightM: e.heightM, time: dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) })
  })
  return Object.keys(byDay).sort().map(k => byDay[k])
}

function fmtDay(iso) {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })
}

function windTone(kn) {
  if (kn == null) return 'var(--muted)'
  if (kn < 11) return '#137050'
  if (kn < 22) return '#8a5c0a'
  if (kn < 34) return '#e0884f'
  return '#b4313c'
}
function waveTone(m) {
  if (m == null) return 'var(--muted)'
  if (m < 0.5) return '#137050'
  if (m < 1.25) return '#8a5c0a'
  if (m < 2.5) return '#e0884f'
  return '#b4313c'
}

function Arrow({ deg, size = 14 }) {
  if (deg == null) return null
  return <span style={{ display: 'inline-block', transform: 'rotate(' + ((deg + 180) % 360) + 'deg)', fontSize: size, lineHeight: 1 }}>{'\u2191'}</span>
}

const fmt = v => (v == null ? '\u2013' : (Math.round(v * 10) / 10))

function Metric({ label, value, sub, tone, arrow }) {
  return (
    <div style={{ minWidth: 78 }}>
      <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: tone || 'var(--text)', display: 'flex', alignItems: 'center', gap: 4 }}>
        {value}{arrow != null && <Arrow deg={arrow} />}
      </div>
      {sub && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

const LAYERS = [
  { id: 'wind', label: 'Wind' },
  { id: 'gust', label: 'Gusts' },
  { id: 'waves', label: 'Waves' },
  { id: 'swell1', label: 'Swell' },
  { id: 'currents', label: 'Currents' },
  { id: 'pressure', label: 'Pressure' },
  { id: 'rain', label: 'Rain' },
  { id: 'temp', label: 'Temp' },
]

const card = { background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 12, padding: 18 }
const sel = { background: 'var(--hull)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 12, padding: '7px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }
const gridRow = { display: 'grid', gridTemplateColumns: '1.1fr 1.2fr 1fr 1.1fr 0.8fr 0.8fr 0.8fr', gap: 8, padding: '11px 16px', alignItems: 'center' }

export default function Weather() {
  const { can } = useAuth()
  const [ports, setPorts] = useState([])
  const [query, setQuery] = useState('')
  const [sugs, setSugs] = useState([])
  const [showSugs, setShowSugs] = useState(false)
  const [selected, setSelected] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [layer, setLayer] = useState('waves')
  const [tides, setTides] = useState(null)
  const [tidesLoading, setTidesLoading] = useState(false)
  const [tidesErr, setTidesErr] = useState('')
  const [briefing, setBriefing] = useState('')
  const [briefRange, setBriefRange] = useState(0)
  const [briefAt, setBriefAt] = useState(null)
  const [briefLoading, setBriefLoading] = useState(false)
  const [briefErr, setBriefErr] = useState('')

  useEffect(() => {
    fetchRows('port_calls').then(rows => {
      const list = rows
        .map(r => ({ port: getVal(r['Port']), country: getVal(r['Country']), status: getVal(r['Status']), eta: getVal(r['ETA Date']) }))
        .filter(p => p.port)
      setPorts(list)
    }).catch(() => {})
  }, [])

  async function suggest(text) {
    const t = (text || '').trim()
    if (t.length < 2) { setSugs([]); return }
    try {
      const r = await fetch('https://geocoding-api.open-meteo.com/v1/search?name=' + encodeURIComponent(t) + '&count=6&language=en&format=json')
      const d = await r.json()
      setSugs((d.results || []).map(x => ({
        label: [x.name, x.admin1, x.country].filter(Boolean).join(', '),
        lat: x.latitude, lon: x.longitude, name: x.name,
      })))
    } catch (e) { setSugs([]) }
  }

  let _sugTimer = null
  function onType(v) {
    setQuery(v); setShowSugs(true)
    clearTimeout(_sugTimer)
    _sugTimer = setTimeout(() => suggest(v), 250)
  }

  async function lookupCoords(lat, lon, name) {
    setShowSugs(false); setSugs([])
    setLoading(true); setError(''); setData(null); setBriefing(''); setBriefAt(null); setTides(null); setTidesErr('')
    try {
      const r = await fetch('/api/weather?lat=' + lat + '&lon=' + lon + '&name=' + encodeURIComponent(name || ''))
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'Lookup failed')
      setData(d)
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }

  async function lookup(port, country) {
    if (!port) return
    setLoading(true); setError(''); setData(null); setTides(null); setTidesErr('')
    try {
      const params = new URLSearchParams({ port })
      if (country) params.set('country', country)
      const r = await fetch('/api/weather?' + params.toString())
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'Lookup failed')
      setData(d)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  function onSelectPort(val) {
    setSelected(val)
    if (!val) return
    const p = ports.find(x => (x.port + '|' + x.country) === val)
    if (p) { setQuery(p.port); lookup(p.port, p.country) }
  }

  const [posting, setPosting] = useState(false)
  const [posted, setPosted] = useState(false)
  const [tidesPosting, setTidesPosting] = useState(false)
  const [tidesPosted, setTidesPosted] = useState(false)

  async function postToBulletins() {
    if (!briefing || !data) return
    const dateLabel = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    if (!window.confirm('Post this weather briefing to the crew Bulletins board?')) return
    setPosting(true)
    try {
      await createRow('announcements', {
        Title: 'Weather Briefing \u2014 ' + data.location + ' (' + dateLabel + ')',
        Body: briefing,
        Category: 'Weather',
        Priority: 'Normal',
        'Posted by': 'Weather',
        'Posted date': new Date().toISOString().split('T')[0],
        Active: true,
      })
      setPosted(true)
      setTimeout(() => setPosted(false), 4000)
    } catch (e) {
      alert('Could not post: ' + e.message)
    } finally {
      setPosting(false)
    }
  }

  async function postTidesToBulletins() {
    if (!tides || !tides.available || !data) return
    const dateLabel = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    if (!window.confirm('Post these tide times to the crew Bulletins board?')) return
    setTidesPosting(true)
    try {
      const lines = groupTides(tides.extremes).slice(0, 5).map(g => {
        const ev = g.events.map(e => (e.type === 'High' ? 'H' : 'L') + ' ' + e.time + ' (' + e.heightM + 'm)').join('   ')
        return g.day + ':  ' + ev
      })
      const body = (tides.station ? tides.station + '\n' : '')
        + 'Tidal range ' + (Math.round(tides.tidalRangeM * 100) / 100) + ' m\n\n'
        + lines.join('\n')
      await createRow('announcements', {
        Title: 'Tides \u2014 ' + data.location + ' (' + dateLabel + ')',
        Body: body,
        Category: 'Tides',
        Priority: 'Normal',
        'Posted by': 'Tides',
        'Posted date': new Date().toISOString().split('T')[0],
        Active: true,
      })
      setTidesPosted(true)
      setTimeout(() => setTidesPosted(false), 4000)
    } catch (e) {
      alert('Could not post: ' + e.message)
    } finally {
      setTidesPosting(false)
    }
  }

  useEffect(() => {
    // tides cache restore
    if (!data) return
    const today = new Date().toISOString().split('T')[0]
    const k = data.latitude.toFixed(3) + ',' + data.longitude.toFixed(3) + ',' + today
    if (tideCache[k]) { setTides(tideCache[k]); return }
    try {
      const ls = localStorage.getItem('hc_tides_' + k)
      if (ls) { const v = JSON.parse(ls); tideCache[k] = v; setTides(v) }
    } catch (e) {}
  }, [data])

  async function getTides() {
    if (!data) return
    const today = new Date().toISOString().split('T')[0]
    const cacheKey = data.latitude.toFixed(3) + ',' + data.longitude.toFixed(3) + ',' + today
    if (tideCache[cacheKey]) { setTides(tideCache[cacheKey]); return }
    try {
      const ls = localStorage.getItem('hc_tides_' + cacheKey)
      if (ls) { const v = JSON.parse(ls); tideCache[cacheKey] = v; setTides(v); return }
    } catch (e) {}
    setTidesLoading(true); setTidesErr(''); setTides(null)
    try {
      const r = await fetch('/api/tides?lat=' + data.latitude + '&lon=' + data.longitude)
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'Tides lookup failed')
      tideCache[cacheKey] = d
      try { localStorage.setItem('hc_tides_' + cacheKey, JSON.stringify(d)) } catch (e) {}
      setTides(d)
    } catch (e) {
      setTidesErr(e.message)
    } finally {
      setTidesLoading(false)
    }
  }

  async function getBriefing(rangeDays = briefRange) {
    if (!data) return
    setBriefLoading(true); setBriefErr(''); setBriefing('')
    try {
      const ss = await import('../lib/supabase')
      const sess = await ss.supabase.auth.getSession()
      const hdr = sess.data.session ? { Authorization: 'Bearer ' + sess.data.session.access_token } : {}
      const r = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...hdr },
        body: JSON.stringify({ action: 'weather_briefing', location: data.location, current: data.current, days: (rangeDays && rangeDays > 0) ? data.days.slice(0, rangeDays) : data.days }),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || d.detail || 'Briefing failed')
      setBriefing(d.briefing || '')
      setBriefAt(new Date())
    } catch (e) {
      setBriefErr(e.message)
    } finally {
      setBriefLoading(false)
    }
  }

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
        {ports.length > 0 && (
          <select value={selected} onChange={e => onSelectPort(e.target.value)} style={sel}>
            <option value="">Upcoming port calls{'\u2026'}</option>
            {ports.map((p, i) => (
              <option key={i} value={p.port + '|' + p.country}>
                {p.port}{p.country ? ', ' + p.country : ''}{p.eta ? ' \u2014 ' + p.eta : ''}
              </option>
            ))}
          </select>
        )}
        <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
          <input
            value={query}
            onChange={e => onType(e.target.value)}
            onFocus={() => query.length >= 2 && setShowSugs(true)}
            onBlur={() => setTimeout(() => setShowSugs(false), 150)}
            onKeyDown={e => { if (e.key === 'Enter') { setShowSugs(false); lookup(query.trim()) } }}
            placeholder="Search any port or city (e.g. West Palm Beach)..."
            className="search-inp"
            style={{ width: '100%' }}
          />
          {showSugs && sugs.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 9999, background: 'var(--ink2)', border: '0.5px solid var(--border2)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', maxHeight: 280, overflowY: 'auto' }}>
              {sugs.map((sg, i) => (
                <div key={i} onMouseDown={() => { setQuery(sg.name); lookupCoords(sg.lat, sg.lon, sg.label) }}
                  style={{ padding: '9px 12px', fontSize: 12.5, color: 'var(--text)', cursor: 'pointer', borderBottom: i < sugs.length - 1 ? '0.5px solid var(--border)' : 'none' }}>
                  {sg.label}
                </div>
              ))}
            </div>
          )}
        </div>
        {can && !can('weather', 'write') ? <div style={{ fontSize:12, color:'var(--muted)', padding:'8px 12px' }}>Read only — forecast generation not permitted</div> : <button className="btn btn-primary" onClick={() => { setShowSugs(false); lookup(query.trim()) }} disabled={loading || !query.trim()}>{loading ? 'Loading...' : 'Get forecast'}</button>}
      </div>

      {error && <div style={{ color: '#b4313c', fontSize: 13, padding: 12 }}>{error}</div>}

      {data && (
        <>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{data.location}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>{data.latitude?.toFixed(3)}, {data.longitude?.toFixed(3)}</span>
            {(!can || can('weather','write')) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: 'var(--muted)', marginRight: 2 }}>{briefLoading ? 'Analyzing\u2026' : '\u2693 AI Briefing:'}</span>
                {[{l:'Today',d:1},{l:'2 days',d:2},{l:'3 days',d:3},{l:'Week',d:0}].map(opt => (
                  <button key={opt.l} onClick={() => { setBriefRange(opt.d); getBriefing(opt.d) }} disabled={briefLoading}
                    style={{ fontSize: 11, padding: '4px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                      border: '0.5px solid ' + (briefRange === opt.d ? 'var(--gold2)' : 'var(--border)'),
                      background: briefRange === opt.d ? 'rgba(201,147,58,.18)' : 'var(--hull)',
                      color: briefRange === opt.d ? 'var(--gold2)' : 'var(--muted)' }}>
                    {opt.l}
                  </button>
                ))}
              </div>
            )}
          </div>
          {briefErr && <div style={{ color: '#b4313c', fontSize: 12, marginBottom: 12 }}>{briefErr}</div>}
          {briefing && (
            <div style={{ ...card, marginBottom: 14, borderLeft: '2px solid var(--gold2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
                <div style={{ fontSize: 10, color: 'var(--gold2)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>Captain's Briefing{briefAt ? ' \u00b7 ' + briefAt.toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                <button onClick={postToBulletins} disabled={posting}
                  style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--border)', background: 'var(--hull)', color: posted ? '#137050' : 'var(--muted)', whiteSpace: 'nowrap' }}>
                  {posting ? 'Posting\u2026' : posted ? '\u2713 Posted' : 'Post to Bulletins'}
                </button>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{briefing}</div>
            </div>
          )}

          <div style={card}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
              <div style={{ minWidth: 120 }}>
                <div style={{ fontSize: 34, fontWeight: 600, color: 'var(--text)', lineHeight: 1 }}>{fmt(data.current.tempC)}{'\u00b0'}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{data.current.conditions}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>Feels {fmt(data.current.feelsC)}{'\u00b0'}</div>
              </div>
              <Metric label="Wind" value={fmt(data.current.windKn) + ' kn'} sub={data.current.windCompass} tone={windTone(data.current.windKn)} arrow={data.current.windDir} />
              <Metric label="Gusts" value={fmt(data.current.gustKn) + ' kn'} tone={windTone(data.current.gustKn)} />
              <Metric label="Waves" value={fmt(data.current.waveM) + ' m'} sub={data.current.waveCompass + (data.current.wavePeriodS ? ' \u00b7 ' + fmt(data.current.wavePeriodS) + 's' : '')} tone={waveTone(data.current.waveM)} arrow={data.current.waveDir} />
              <Metric label="Swell" value={fmt(data.current.swellM) + ' m'} sub={data.current.swellCompass + (data.current.swellPeriodS ? ' \u00b7 ' + fmt(data.current.swellPeriodS) + 's' : '')} tone={waveTone(data.current.swellM)} arrow={data.current.swellDir} />
              <Metric label="Pressure" value={fmt(data.current.pressureHpa) + ' hPa'} />
              <Metric label="Visibility" value={data.current.visibilityM != null ? (data.current.visibilityM / 1000).toFixed(0) + ' km' : '\u2013'} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14, marginBottom: 8 }}>
            {LAYERS.map(l => (
              <button key={l.id} onClick={() => setLayer(l.id)}
                style={{
                  fontSize: 12, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                  border: '0.5px solid ' + (layer === l.id ? 'var(--gold2)' : 'var(--border)'),
                  background: layer === l.id ? 'rgba(201,147,58,.12)' : 'var(--hull)',
                  color: layer === l.id ? 'var(--gold2)' : 'var(--muted)',
                }}>{l.label}</button>
            ))}
          </div>
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <iframe
              key={data.latitude + ',' + data.longitude + ',' + layer}
              title="Windy marine map"
              width="100%"
              height="440"
              style={{ border: 0, display: 'block' }}
              src={'https://embed.windy.com/embed2.html?lat=' + data.latitude + '&lon=' + data.longitude + '&detailLat=' + data.latitude + '&detailLon=' + data.longitude + '&zoom=7&level=surface&overlay=' + layer + '&menu=&message=&marker=true&calendar=now&pressure=true&type=map&location=coordinates&detail=&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1'}
            />
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 4, textAlign: 'right' }}>{OSM_TEXT} · Weather map: Windy.com</div>

          <div style={{ ...card, marginTop: 14, padding: 0, overflow: 'hidden' }}>
            <div style={{ ...gridRow, background: 'var(--hull)', borderBottom: '0.5px solid var(--border)', fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>
              <span>Day</span><span>Conditions</span><span>Temp</span><span>Wind</span><span>Gust</span><span>Wave</span><span>Swell</span>
            </div>
            {data.days.map((d, i) => (
              <div key={i} style={{ ...gridRow, borderBottom: i < data.days.length - 1 ? '0.5px solid var(--border)' : 'none' }}>
                <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500 }}>{fmtDay(d.date)}</span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{d.conditions}</span>
                <span style={{ fontSize: 12, color: 'var(--text)' }}>{fmt(d.tempMaxC)}{'\u00b0'} / {fmt(d.tempMinC)}{'\u00b0'}</span>
                <span style={{ fontSize: 12, color: windTone(d.windMaxKn) }}>{fmt(d.windMaxKn)} kn <span style={{ color: 'var(--muted)' }}>{d.windCompass}</span></span>
                <span style={{ fontSize: 12, color: windTone(d.gustMaxKn) }}>{fmt(d.gustMaxKn)} kn</span>
                <span style={{ fontSize: 12, color: waveTone(d.waveMaxM) }}>{fmt(d.waveMaxM)} m</span>
                <span style={{ fontSize: 12, color: waveTone(d.swellMaxM) }}>{fmt(d.swellMaxM)} m</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14 }}>
            {!tides && !tidesLoading && (
              <button onClick={getTides} style={{ fontSize: 12, padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--gold2)', background: 'rgba(201,147,58,.12)', color: 'var(--gold2)' }}>
                {'\uD83C\uDF0A'} Show Tides
              </button>
            )}
            {tidesLoading && <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 0' }}>Loading tides{'\u2026'}</div>}
            {tidesErr && <div style={{ fontSize: 12, color: '#b4313c', padding: '8px 0' }}>{tidesErr}</div>}
            {tides && tides.available === false && (
              <div style={{ ...card }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>No tidal data available for this location.</div>
              </div>
            )}
            {tides && tides.available && tides.negligible && (
              <div style={{ ...card }}>
                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600, marginBottom: 6 }}>Tides{tides.station ? ' · ' + tides.station : ''}</div>
                <div style={{ fontSize: 13, color: 'var(--text)' }}>Minimal tidal range here ({fmt(tides.tidalRangeM)} m) {'\u2014'} tides are not significant at this location.</div>
              </div>
            )}
            {tides && tides.available && !tides.negligible && (
              <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '0.5px solid var(--border)' }}>
                  <div style={{ fontSize: 10, color: 'var(--gold2)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>Tides{tides.station ? ' · ' + tides.station : ''}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>Range {fmt(tides.tidalRangeM)} m</span>
                    <button onClick={postTidesToBulletins} disabled={tidesPosting}
                      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--border)', background: 'var(--hull)', color: tidesPosted ? '#137050' : 'var(--muted)', whiteSpace: 'nowrap' }}>
                      {tidesPosting ? 'Posting\u2026' : tidesPosted ? '\u2713 Posted' : 'Post to Bulletins'}
                    </button>
                  </div>
                </div>
                <div style={{ padding: '8px 12px 0' }}>
                  <TideCurve extremes={tides.extremes} />
                </div>
                {groupTides(tides.extremes).slice(0, 5).map((g, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 16px', borderBottom: i < 4 ? '0.5px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', minWidth: 90 }}>{g.day}</span>
                    {g.events.map((e, j) => (
                      <span key={j} style={{ fontSize: 12, color: 'var(--muted)' }}>
                        <span style={{ color: e.type === 'High' ? '#2a6a96' : '#8a5c0a', fontWeight: 600 }}>{e.type === 'High' ? 'H' : 'L'}</span> {e.time} <span style={{ opacity: .7 }}>{e.heightM} m</span>
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 10 }}>Marine data via Open-Meteo {'\u00b7'} updates every 30 min{tides && tides.available ? ' · tides via WorldTides' : ''}</div>
        </>
      )}

      {!data && !loading && !error && (
        <div style={{ color: 'var(--muted)', fontSize: 13, padding: 20 }}>
          Pick an upcoming port call or search any port to see a marine forecast.
        </div>
      )}
    </div>
  )
}
